import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Gamepad2, Crosshair, Globe2, Wrench, Cpu, Rocket, ChevronRight, Siren, CircuitBoard, Radio, Joystick } from 'lucide-react';
import { Badge } from '../ui/badge';
import { hobbies } from '../../data/mock';

const iconMap = {
  Gamepad2, Crosshair, Globe2, Wrench, Cpu, Rocket, Siren, CircuitBoard, Radio, Joystick,
};

function GameCard({ game, index }) {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 15 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ delay: index * 0.06 }}
      className={`group rounded-lg border p-4 transition-all ${
        game.current
          ? 'border-emerald-500/25 bg-emerald-500/[0.04] hover:border-emerald-500/40'
          : 'border-[#1a1a2e] bg-[#0a0a14]/60 hover:border-[#252540]'
      }`}
    >
      <div className="flex items-center gap-3">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${
          game.current
            ? 'bg-emerald-500/15 border border-emerald-500/25'
            : 'bg-[#111120] border border-[#1a1a2e]'
        }`}>
          <Crosshair size={16} className={game.current ? 'text-emerald-400' : 'text-zinc-500 group-hover:text-zinc-400 transition-colors'} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-mono text-sm text-zinc-200 truncate">{game.name}</span>
            {game.current && (
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse shrink-0" />
            )}
          </div>
          <span className="font-mono text-[10px] text-zinc-600">{game.genre}</span>
        </div>
      </div>
    </motion.div>
  );
}

function BuildCard({ build, index }) {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const Icon = iconMap[build.icon] || CircuitBoard;

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 15 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ delay: index * 0.08 }}
      className="rounded-xl border border-[#1a1a2e] bg-[#0a0a14]/60 p-5 hover:border-[#252540] transition-colors group"
    >
      <div className="flex items-start justify-between mb-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
          build.status === 'WIP'
            ? 'bg-amber-500/10 border border-amber-500/20'
            : 'bg-cyan-500/10 border border-cyan-500/20'
        }`}>
          <Icon size={18} className={build.status === 'WIP' ? 'text-amber-400' : 'text-cyan-400'} />
        </div>
        <Badge
          variant="outline"
          className={`font-mono text-[9px] px-2 py-0.5 ${
            build.status === 'WIP'
              ? 'bg-amber-500/10 border-amber-500/20 text-amber-400'
              : 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400'
          }`}
        >
          {build.status === 'WIP' ? 'IN PROGRESS' : 'BUILT'}
        </Badge>
      </div>
      <h4 className="font-semibold text-zinc-200 text-sm mb-1" style={{ fontFamily: 'Figtree, sans-serif' }}>
        {build.name}
      </h4>
      <p className="text-zinc-500 text-xs leading-relaxed" style={{ fontFamily: 'Figtree, sans-serif' }}>
        {build.description}
      </p>
      {build.version && (
        <span className="inline-block mt-3 font-mono text-[10px] text-zinc-600 bg-[#111120] border border-[#1a1a2e] px-2 py-1 rounded">
          {build.version}
        </span>
      )}
    </motion.div>
  );
}

export default function OffDuty() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  return (
    <section id="offduty" className="relative py-24">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <motion.div ref={ref} initial={{ opacity: 0, y: 20 }} animate={inView ? { opacity: 1, y: 0 } : {}} className="mb-12">
          <div className="flex items-center gap-3 mb-3">
            <Gamepad2 size={20} className="text-emerald-400" />
            <span className="font-mono text-xs text-emerald-400/60 uppercase tracking-widest">// Off-Duty</span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-zinc-100" style={{ fontFamily: 'Figtree, sans-serif' }}>
            When I'm Not Coding
          </h2>
          <p className="text-zinc-500 mt-2 max-w-lg text-base" style={{ fontFamily: 'Figtree, sans-serif' }}>
            {hobbies.aboutMe}
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-10">
          {/* Gaming */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ delay: 0.2 }}
          >
            <div className="flex items-center gap-3 mb-5">
              <div className="w-8 h-8 rounded-lg bg-rose-500/10 border border-rose-500/20 flex items-center justify-center">
                <Crosshair size={15} className="text-rose-400" />
              </div>
              <div>
                <h3 className="font-semibold text-zinc-200 text-sm" style={{ fontFamily: 'Figtree, sans-serif' }}>Gaming Arena</h3>
                <p className="font-mono text-[10px] text-zinc-600">FPS & OPEN WORLD ENTHUSIAST</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              {hobbies.games.map((game, i) => (
                <GameCard key={game.name} game={game} index={i} />
              ))}
            </div>

            <div className="mt-4 p-3 rounded-lg bg-[#0a0a14]/40 border border-[#1a1a2e]/50">
              <p className="font-mono text-xs text-zinc-500 leading-relaxed">
                <span className="text-emerald-400/70">{'>'}</span> Mostly into <span className="text-zinc-300">FPS</span> and <span className="text-zinc-300">open-world</span> games. The thrill of strategy meets fast reflexes.
              </p>
            </div>
          </motion.div>

          {/* Electronics / Building */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ delay: 0.3 }}
          >
            <div className="flex items-center gap-3 mb-5">
              <div className="w-8 h-8 rounded-lg bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center">
                <CircuitBoard size={15} className="text-cyan-400" />
              </div>
              <div>
                <h3 className="font-semibold text-zinc-200 text-sm" style={{ fontFamily: 'Figtree, sans-serif' }}>Crafting Station</h3>
                <p className="font-mono text-[10px] text-zinc-600">HARDWARE & ELECTRONICS BUILDS</p>
              </div>
            </div>

            {/* Featured build — FPV Drone (big card) */}
            {hobbies.builds.filter(b => b.status === 'WIP').map((build, i) => (
              <BuildCard key={build.name} build={build} index={i} />
            ))}

            {/* Completed builds — compact side by side */}
            <div className="grid grid-cols-2 gap-3 mt-3">
              {hobbies.builds.filter(b => b.status === 'DONE').map((build, i) => {
                const Icon = iconMap[build.icon] || CircuitBoard;
                return (
                  <motion.div
                    key={build.name}
                    initial={{ opacity: 0, y: 10 }}
                    animate={inView ? { opacity: 1, y: 0 } : {}}
                    transition={{ delay: 0.4 + i * 0.08 }}
                    className="rounded-lg border border-[#1a1a2e] bg-[#0a0a14]/60 p-3.5 hover:border-[#252540] transition-colors group"
                  >
                    <div className="flex items-center gap-2.5 mb-2">
                      <div className="w-8 h-8 rounded-md bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center">
                        <Icon size={14} className="text-cyan-400" />
                      </div>
                      <Badge variant="outline" className="font-mono text-[8px] px-1.5 py-0 bg-emerald-500/10 border-emerald-500/20 text-emerald-400">
                        BUILT
                      </Badge>
                    </div>
                    <h4 className="font-semibold text-zinc-200 text-sm mb-1" style={{ fontFamily: 'Figtree, sans-serif' }}>{build.name}</h4>
                    <p className="text-zinc-600 text-[11px] leading-relaxed" style={{ fontFamily: 'Figtree, sans-serif' }}>{build.description}</p>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
